import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Checkbox, MenuItem, Select, TextField } from "@mui/material";
import { showError, showToast } from "../../constants/toasts";
import {
  useAddDocMutation,
  useLazyGetDocByIdQuery,
  useUpdateDocMutation,
} from "../../services/doc";
import * as Yup from "yup";
import "react-phone-input-2/lib/bootstrap.css";
import { Form, Formik } from "formik";

const validationSchema = Yup.object().shape({
  tag_set: Yup.string().required("Tag Set is required"),
  result_type: Yup.string(),
  user_feedback_option: Yup.boolean().default(false),
});

const initialValue = {
  tag_set: "",
  result_type: "default",
  user_feedback_option: false,
};

const AddDocument = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [tagSet, setTagSet] = useState<string>("");
  const [summarization, setSummarization] = React.useState<string>("");
  const [processedSummary, setProcessedSummary] = React.useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  type DocumentItem = {
    id: number;
    tag_set: string;
    summarization: string;
    processed_summary: string;
    is_default?: boolean;
    is_active?: boolean;
    result_type?: string;
    user_feedback_option?: boolean;
  };
  const [data, setData] = useState<DocumentItem | null>(null);
  const [getDocById] = useLazyGetDocByIdQuery();
  const [addDoc] = useAddDocMutation();
  const [updateDoc] = useUpdateDocMutation();
  const { id } = useParams();

  const fetchDoc = async () => {
    if (id) {
      try {
        const response = await getDocById({ id: Number(id) }).unwrap();
        if (response?.status === 200) {
          const item: DocumentItem | undefined = Array.isArray(response.data)
            ? response.data[0]
            : response.data;

          if (item) {
            setData(item);
            setTagSet(item.tag_set ?? "");
            setSummarization(item.summarization ?? "");
            setProcessedSummary(item.processed_summary ?? "");
          }
        }
      } catch (e: any) {
        showError(e?.data?.message || "Failed to load document");
      }
    } else {
      setTagSet("");
      setSummarization("");
      setProcessedSummary("");
    }
  };
  useEffect(() => {
    fetchDoc();
  }, [id]);

  const handleSave = async (body: any) => {
    setError(true);
    // const trimmedTagSet = tagSet.trim();
    // const trimmedSummarization = summarization.trim();
    // // const trimmedProcessedSummary = processedSummary.trim();
    // if (!trimmedTagSet || !trimmedSummarization) {
    //   showError("All fields are required");
    //   return;
    // }

    setIsLoading(true);
    try {
      // const body: any = {
      //   tag_set: trimmedTagSet,
      //   summarization: trimmedSummarization,
      //   // processed_summary: trimmedProcessedSummary,
      // };
      if (id) {
        const idNum = parseInt(id as string, 10);
        const res = await updateDoc({ body, id: idNum }).unwrap();
        if ((res as any)?.status === 200 || (res as any)?.status === 201) {
          showToast("Document updated successfully");
          navigate(-1);
        }
      } else {
        const res = await addDoc({ body }).unwrap();
        if ((res as any)?.status === 200 || (res as any)?.status === 201) {
          showToast("Document added successfully");
          navigate(-1);
        }
      }
    } catch (e: any) {
      showError(e?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Add Document and OCR Processing</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/document-processing")}>
            Document Processing
          </span>{" "}
          - Add Document and OCR Processing
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <Formik
              initialValues={data ? data : initialValue}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                handleSave(values);
                setSubmitting(false);
              }}
            >
              {({
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
                values,
                errors,
                touched,
                setFieldValue,
              }) => (
                <Form>
                  <div className="form_container">
                    <div className="control_group w_33 my-2">
                      <label>Tag Set</label>
                      <TextField
                        placeholder="Enter"
                        hiddenLabel
                        fullWidth
                        name="tag_set"
                        value={values.tag_set}
                        onChange={handleChange("tag_set")}
                      />
                      {errors && errors.tag_set ? (
                        <h6 className="err_msg">Tag Set is required</h6>
                      ) : (
                        ""
                      )}
                    </div>
                    {/* <div className="control_group w_33">
                    <label>Summarization</label>
                    <TextField
                      placeholder="Enter"
                      hiddenLabel
                      fullWidth
                      value={values.summarization}
                      onChange={handleChange("summarization")}
                    />
                    {errors && errors.summarization ? (
                      <h6 className="err_msg">Summarization is required</h6>
                    ) : (
                      ""
                    )}
                  </div> */}
                    <div className="control_group w_33">
                      <label>Result Type</label>
                      <Select
                        name="result_type"
                        value={values.result_type}
                        onChange={(e) =>
                          setFieldValue("result_type", e.target.value)
                        }
                        className="select_result"
                      >
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="paragraph">Paragraph</MenuItem>
                        <MenuItem value="bullet">Bullet Points</MenuItem>
                        <MenuItem value="ordered">Ordered List</MenuItem>
                        <MenuItem value="unordered">Unordered List</MenuItem>
                      </Select>
                    </div>
                    <div className="control_group w_33">
                      <label>User Feedback Option</label>
                      <Checkbox name="user_feedback_option" />
                    </div>
                  </div>
                  <div className="form_btn">
                    <Button
                      className="btn btn_primary"
                      disabled={isLoading}
                      type="submit"
                    >
                      {isLoading ? "Processing..." : id ? "Update" : "Save"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
            {/* <div className="control_group w_33">
              <label>Processed Summary</label>
              <TextField
                placeholder="Enter "
                fullWidth
                hiddenLabel
                multiline
                maxRows={5}
                minRows={5}
                value={processedSummary}
                onChange={(e) => setProcessedSummary(e.target.value)}
              />
              {error && !processedSummary.trim() ? (
                <h6 className="err_msg">Processed Summary is required</h6>
              ) : (
                ""
              )}
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddDocument;
